import { useEffect } from 'react';
import { doc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * useTrackVisit — compteur de visites journalières
 *
 * Logique :
 * - 1 visite comptabilisée par appareil et par jour (clé = date du jour)
 * - Si l'utilisateur revient le lendemain → recompté (nouvelle date)
 * - Utilise localStorage (persiste entre sessions) + clé datée
 *
 * Structure Firestore /stats/visitors :
 *   { total: number, days: { "YYYY-MM-DD": number }, lastVisit: timestamp }
 */
export const useTrackVisit = () => {
  useEffect(() => {
    const today      = new Date().toISOString().slice(0, 10); // "2026-03-16"
    const STORAGE_KEY = `yeldi_visited_${today}`;             // clé unique par jour

    // Déjà compté aujourd'hui sur cet appareil
    if (localStorage.getItem(STORAGE_KEY)) return;

    const ref = doc(db, 'stats', 'visitors');

    const track = async () => {
      try {
        // updateDoc supporte la notation pointée pour sous-champs
        await updateDoc(ref, {
          total:             increment(1),
          [`days.${today}`]: increment(1),
          lastVisit:         serverTimestamp(),
        });
      } catch (err) {
        // Document inexistant → le créer
        if (err.code === 'not-found' || err.code === 'firestore/not-found') {
          await setDoc(ref, {
            total:     1,
            days:      { [today]: 1 },
            lastVisit: serverTimestamp(),
          });
        }
      }
    };

    track();
    localStorage.setItem(STORAGE_KEY, '1');

    // Nettoyage : supprimer les clés des jours précédents pour ne pas
    // surcharger le localStorage (garde seulement les 7 derniers jours)
    try {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('yeldi_visited_') && key !== STORAGE_KEY) {
          const keyDate = key.replace('yeldi_visited_', '');
          const diff = (new Date(today) - new Date(keyDate)) / 86400000;
          if (diff > 7) localStorage.removeItem(key);
        }
      });
    } catch { /* silencieux */ }
  }, []);
};